import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
const API_URL = process.env.REACT_APP_API_URL;

const AdminDashboard = () => {
  const [orgName, setOrgName] = useState('');
  const [orgCode, setOrgCode] = useState('');
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const handleAuthError = useAuthErrorHandler();

  useEffect(() => {
    const fetchUserRoleAndUsers = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login', { replace: true });
        return;
      }

      try {
        const roleRes = await fetch(`${API_URL}/auth/get_role`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (handleAuthError(roleRes)) return;
        if (!roleRes.ok) throw new Error('Failed to verify role');

        const roleData = await roleRes.json();
        if (roleData.role !== 'site_admin') {
          navigate('/', { replace: true });
          return;
        }

        const usersRes = await fetch(`${API_URL}/auth/get_users`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (handleAuthError(usersRes)) return;
        if (!usersRes.ok) throw new Error('Failed to fetch users');

        const usersData = await usersRes.json();
        setUsers(usersData);
      } catch (err) {
        setError(err.message);
      }
    };

    fetchUserRoleAndUsers();
  }, [navigate, handleAuthError]);

  const handleOrgSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${API_URL}/org/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: orgName, code: orgCode }),
      });

      if (handleAuthError(response)) return;
      if (!response.ok) throw new Error('Failed to create organization');

      setOrgName('');
      setOrgCode('');
      alert('Organization created successfully');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUserRoleChange = async (userId, newRole) => {
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${API_URL}/auth/update_role`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ new_role: newRole, user_id: userId}),
      });

      if (handleAuthError(response)) return;
      if (!response.ok) throw new Error('Failed to update user role');

      setUsers(users.map(user => user.id === userId ? { ...user, role: newRole } : user));
      alert('User role updated successfully');
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="container mx-auto p-8 max-w-3xl">
      <h2 className="text-2xl font-semibold mb-6">Admin Dashboard</h2>
      {error && <div className="text-red-500 mb-4">{error}</div>}

      <section className="mb-10">
        <h3 className="text-xl font-semibold mb-4">Create Organization</h3>
        <form onSubmit={handleOrgSubmit} className="space-y-4">
          <input
            type="text"
            placeholder="Organization Name"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            className="w-full border rounded px-3 py-2"
            required
          />
          <input
            type="text"
            placeholder="Organization Code"
            value={orgCode}
            onChange={(e) => setOrgCode(e.target.value)}
            className="w-full border rounded px-3 py-2"
            required
          />
          <button className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition">
            Create
          </button>
        </form>
      </section>

      <section>
        <h3 className="text-xl font-semibold mb-4">Manage Users</h3>
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="border px-4 py-2">User</th>
              <th className="border px-4 py-2">Role</th>
              <th className="border px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td className="border px-4 py-2">{user.first_name + " " + user.last_name}</td>
                <td className="border px-4 py-2">{user.role}</td>
                <td className="border px-4 py-2">
                  <select
                    onChange={(e) => handleUserRoleChange(user.id, e.target.value)}
                    defaultValue={user.role}
                    className="border rounded px-2 py-1"
                  >
                    <option value="rep">Rep</option>
                    <option value="manager">Manager</option>
                    <option value="site_admin">Site Admin</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default AdminDashboard;
